import { countWordsIncludingMarkup } from './wordcount'
import { normalizeLanguagePairValue } from './languagePairs'

type ProofreadRow = Record<string, any> | null | undefined

const toTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const getPositiveNumber = (value: unknown): number => {
  const numeric = Number(value || 0)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0
}

export const getSavedProofreadTextForRow = (row: ProofreadRow): string => {
  const proofreadChapter = toTrimmedString(row?.proofread_chapter)
  if (proofreadChapter) {
    return proofreadChapter
  }
  return toTrimmedString(row?.proofread_text)
}

export const hasSavedProofreadForRow = (row: ProofreadRow): boolean => {
  if (getPositiveNumber(row?.proofread_word_count) > 0) {
    return true
  }
  return getSavedProofreadTextForRow(row).length > 0
}

export const hasPendingProofreadReviewForRow = (row: ProofreadRow): boolean => {
  const changeSetId = toTrimmedString(row?.proofread_change_set_id)
  const mode = toTrimmedString(row?.proofread_mode).toLowerCase()
  const status = toTrimmedString(row?.proofread_change_set_status).toLowerCase()
  if (!changeSetId || mode !== 'proposal') return false
  return !['', 'applied', 'superseded', 'discarded'].includes(status)
}

export const hasCompletedProofreadProgressForRow = (row: ProofreadRow): boolean => (
  hasSavedProofreadForRow(row) || hasPendingProofreadReviewForRow(row)
)

export const normalizeProofreadVersionValue = (value: unknown): string => {
  const normalized = normalizeLanguagePairValue(value, { originalValue: 'Original' })
  if (value === undefined || value === null) {
    return normalized || 'Original'
  }
  return normalized
}

export const resolveProofreadWordCountForRow = ({
  row,
  fallbackWordCountField,
  fallbackTextField,
}: {
  row: ProofreadRow
  fallbackWordCountField: string
  fallbackTextField: string
}): number => {
  const savedProofreadText = getSavedProofreadTextForRow(row)
  if (savedProofreadText) {
    return getPositiveNumber(row?.proofread_word_count) || countWordsIncludingMarkup(savedProofreadText)
  }

  return (
    getPositiveNumber(row?.[fallbackWordCountField])
    || countWordsIncludingMarkup(toTrimmedString(row?.[fallbackTextField]))
  )
}
